import React, { FunctionComponent, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Tooltip } from '@common/components'
// import { ConnectedBuyFormatCurrency } from '@buy/connectedComponents'
import { get, isEmpty } from 'lodash-es'
import { BillingPeriodOption, OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { BillingPeriodKey, BillingPeriod, BillingPeriodKeyMapper } from '@common/constants'
import { BillingPeriods } from '@buy/state/price'
import { getPrice } from '@buy/utils/volume-components-helpers'
import { ParamTypes } from '@buy/Routes'
import { useParams } from 'react-router-dom'
import { getCheckoutSessionState, setCheckoutSessionState, SessionKeys } from '@buy/utils'
import styles from '../../../../common/components/ToggleButton/toggle-button.module.css'

export interface SwitchProps {
    onChangeFn: (selectedOption: BillingTenureValue) => void
    id: string
    disabled?: boolean
    isLarge?: boolean
    widget?: boolean
    theme?: string
    billingTenure: BillingTenureValue
    setBillingTenure: (data: BillingTenureValue) => void
    tooltipContent: Record<string, string>
    billingPeriods: BillingPeriods[]
    baseLine: OrderSummaryLine
}

export interface BillingTenureValue {
    selected: BillingPeriod
    leftOption?: BillingPeriod
}

export const BillingPeriodSwitch: FunctionComponent<SwitchProps> = ({
    onChangeFn,
    id,
    disabled,
    isLarge,
    widget,
    theme = 'resolve', // theme to be set based on product config in the future
    tooltipContent,
    billingPeriods,
    setBillingTenure,
    billingTenure,
    baseLine,
}) => {
    const { pathProduct, plan } = useParams<ParamTypes>()
    const [leftOption, setLeftOption] = useState<BillingPeriodOption>()
    const [rightOption, setRightOption] = useState<BillingPeriodOption>()
    const [isOn, setIsOn] = useState(true)
    const [percentageSaved, setPercentageSaved] = useState('')

    const modifier = `${styles['wrapper--order-summary']} ${
        billingTenure.leftOption ? styles['wrapper--annual-contract'] : ''
    }`

    useEffect(() => {
        const sessionData = getCheckoutSessionState(pathProduct!, plan!)
        if (!isEmpty(billingPeriods)) {
            const annualBillingFrequency = billingPeriods.find((item) => item.key === BillingPeriodKey.ANNUAL)
            if (isEmpty(annualBillingFrequency) || billingPeriods.length > 2) {
                console.error('Invalid billing period configuration!')
            } else if (billingPeriods.length === 1) setBillingTenure({ selected: BillingPeriod.ANNUAL })
            else {
                const sessionStorageValue: BillingPeriod = get(sessionData, SessionKeys.billingTenure)
                const isValidSessionStorageValue = billingPeriods.find(
                    (item) => sessionStorageValue === BillingPeriodKeyMapper[item.key]
                )
                const leftTenure = billingPeriods.find((item) => item.key !== BillingPeriodKey.ANNUAL)!
                setLeftOption({
                    title: leftTenure.name,
                    key: leftTenure.key,
                    tooltipText: tooltipContent[leftTenure.key],
                    descriptionActive: leftTenure.descriptionActive,
                    descriptionInactive: leftTenure.descriptionInactive,
                })
                setRightOption({
                    title: annualBillingFrequency.name,
                    key: annualBillingFrequency.key,
                    tooltipText: tooltipContent[annualBillingFrequency.key],
                    descriptionActive: annualBillingFrequency.descriptionActive,
                    descriptionInactive: annualBillingFrequency.descriptionInactive,
                })
                const selectedValue =
                    !isEmpty(sessionStorageValue) && isValidSessionStorageValue
                        ? sessionStorageValue
                        : BillingPeriod.ANNUAL
                setBillingTenure({ selected: selectedValue, leftOption: BillingPeriodKeyMapper[leftTenure.key] })
                setIsOn(selectedValue === BillingPeriod.ANNUAL)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingPeriods])

    useEffect(() => {
        if (billingTenure.leftOption) {
            const priceWithoutDiscount = getPrice(1, baseLine.allPrices[billingTenure.leftOption])
            const annualPrice = getPrice(1, baseLine.allPrices[BillingPeriod.ANNUAL])
            const percentage = (
                Number(((priceWithoutDiscount - annualPrice) / priceWithoutDiscount).toFixed(2)) * 100
            ).toFixed(2)
            const floatPercentage = parseFloat(percentage)
            setPercentageSaved(floatPercentage.toString())
        }
    }, [baseLine, billingTenure])

    const handleChange = (isSelected: boolean) => {
        if (onChangeFn && !disabled) {
            const newBillingTenure = {
                selected: isSelected ? BillingPeriod.ANNUAL : BillingPeriodKeyMapper[leftOption!.key],
                leftOption: BillingPeriodKeyMapper[leftOption!.key],
            }
            setCheckoutSessionState(
                newBillingTenure.selected.toString(),
                SessionKeys.billingTenure,
                pathProduct!,
                plan!
            )
            setBillingTenure(newBillingTenure)
            onChangeFn(newBillingTenure)
        }
    }

    const labelClasses = classNames({
        [styles.wrapper]: true,
        [styles[`isOn--${theme}`]]: isOn,
        [styles['wrapper--large']]: isLarge,
        [modifier]: modifier,
        [styles.disabled]: disabled,
        [styles.widget]: widget,
    })

    return (
        <>
            {!isEmpty(billingPeriods) &&
                billingPeriods.length === 2 &&
                !isEmpty(leftOption) &&
                !isEmpty(rightOption) && (
                    <label htmlFor={id} className={labelClasses} data-qat={isOn ? 'toggle-button-isOn' : ''}>
                        <span className={styles.offLabel} data-qat="monthly-label-toggle-element">
                            {leftOption?.title}
                        </span>
                        <span className={styles.subCallout} data-qat="toggle-button-callout">
                            {billingTenure.selected === BillingPeriodKeyMapper[leftOption!.key]
                                ? leftOption?.descriptionActive?.replace('{0}', percentageSaved)
                                : leftOption?.descriptionInactive?.replace('{0}', percentageSaved)}
                        </span>
                        {leftOption?.tooltipText && <Tooltip content={leftOption.tooltipText} />}
                        <input
                            className={styles.input}
                            type="checkbox"
                            onChange={(value) => {
                                setIsOn(value.target.checked)
                                handleChange(value.target.checked)
                            }}
                            name={id}
                            id={id}
                            checked={isOn}
                            data-qat={id}
                        />
                        <span className={styles.check} data-qat="toggle-button-check" />
                        <span className={styles.onLabel} data-qat="monthly-label-toggle-element">
                            <span>{rightOption?.title}</span>
                            <span className={styles.subCallout} data-qat="toggle-button-callout">
                                {billingTenure.selected === BillingPeriodKeyMapper[rightOption!.key]
                                    ? rightOption?.descriptionActive?.replace('{Percentage}', percentageSaved)
                                    : rightOption?.descriptionInactive?.replace('{Percentage}', percentageSaved)}
                            </span>
                        </span>
                        {rightOption?.tooltipText && <Tooltip content={rightOption.tooltipText} />}

                        {/* <span className={styles.callout} data-qat="toggle-button-callout">
                        {content.callout}{' '}
                        {content.discountAmount ? <ConnectedBuyFormatCurrency value={content.discountAmount as number} /> : ''}
                    </span> */}
                    </label>
                )}
        </>
    )
}
